import { useParams } from "react-router-dom"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BasePage from "../../../components/layout/BasePage"
import ModalCheckAction from "../../../components/elements/ModalCheckAction"
import { useContext, useMemo, useState } from "react"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getSingleMajorOrderData } from "../api/apiCalls"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import { useDownloadDocumentMutation } from "../../api/services"
import { MajorOrderGet } from "../../../data/Types"
import BasePreview from "../../../components/elements/BasePreview"
import { API, GeneralQueryKeys, MajorOrderState } from "../../../api/constants"
import { SidePanelContext } from "../../../components/contexts/SidePanelContext"
import MajorOrderDetailInfo from "../components/majorOrder/MajorOrderDetailInfo"
import dayjs from "dayjs"
import MajorOrderDetailHistory from "../components/majorOrder/MajorOrderDetailHistory"
import MajorOrderDetailBillList from "../components/majorOrder/MajorOrderDetailBillList"
import { Tag } from "antd"
import BaseButtonRow from "../../../components/elements/BaseButtonRow"
import { useArchiveMajorOrderMutation, useUpdateMajorOrderMutation } from "../api/services"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"

export default function MajorOrderDetailPage() {
    const { id } = useParams<string>()
    const { data, error, isLoading } = useQuery({
        queryKey: [GeneralQueryKeys.MAJOR_ORDER_SINGLE + id],
        queryFn: () => getSingleMajorOrderData({ id: id || "" }),
    })
    const displayedMajorOrder = useMemo<MajorOrderGet>(() => data, [data])
    const modalCon = useContext(ModalContext)

    // Archive MajorOrder Mutation
    const queryClient = useQueryClient()
    const { mutate: archiveMajorOrder } = useArchiveMajorOrderMutation(queryClient)
    const handleArchiving = () => {
        modalCon?.trigger(0)
        archiveMajorOrder({
            id: id ?? displayedMajorOrder._id,
            archived: !displayedMajorOrder.archived,
        })
    }

    // download file - bill itself or bill attachment
    const { mutate: downloadFile } = useDownloadDocumentMutation()
    const handleDownload = (fileData?: { s3Key: string; filename: string }) => {
        if (fileData) {
            // download bill attachement
            downloadFile({ id: data._id, s3Key: fileData.s3Key, filetype: "bill", filename: fileData.filename })
        } else {
            // download bill itself
            downloadFile({ id: data._id, s3Key: data.s3DocumentRef.s3Key, filetype: "bill", filename: "Rechnung " + data.nr + ".pdf" })
        }
    }

    // current bill

    // preview bill or attachement
    const sidePanelCon = useContext(SidePanelContext)
    const [previewElement, setpreviewElement] = useState<string>()
    const handleChangePreviewElement = (s3Key: string) => {
        setpreviewElement(API + `/bill/${data?._id}/download?s3Key=${s3Key}`)
        sidePanelCon?.trigger(3)
    }

    // UPDATE major order
    const [updatedMajorOrder, setupdatedMajorOrder] = useState<MajorOrderGet>()
    const { mutate: updateMajorOrder } = useUpdateMajorOrderMutation(queryClient)
    const handleUpdateMajorOrder = () => {
        // console.log(updatedMajorOrder)
        if (updatedMajorOrder) updateMajorOrder({ id: id ?? displayedMajorOrder._id, data: updatedMajorOrder as MajorOrderGet })
    }

    return (
        <BasePage>
            <BaseHeadBanner title={"Auftrag vom " + dayjs(displayedMajorOrder?.createdAt).format("DD.MM.YYYY")} />
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !isLoading && !error && (
                <>
                    <BaseButtonRow>
                        <div>
                            <Tag
                                className="text-center w-fit px-8"
                                color={
                                    displayedMajorOrder.state === MajorOrderState.UNBILLED
                                        ? "gray"
                                        : displayedMajorOrder.state === MajorOrderState.PARTIALLY_BILLED
                                        ? "blue"
                                        : "green"
                                }>
                                {displayedMajorOrder.state === MajorOrderState.UNBILLED
                                    ? "Keine Rechnung"
                                    : displayedMajorOrder.state === MajorOrderState.PARTIALLY_BILLED
                                    ? "In Berechnung"
                                    : "Abgerechnet"}
                            </Tag>
                            {displayedMajorOrder.archived && (
                                <Tag className="text-center w-fit px-8" color={"gray"}>
                                    Archiviert
                                </Tag>
                            )}
                        </div>
                    </BaseButtonRow>
                    <BaseGrowBody classProps="overflow-y-scroll overflow-x-hidden">
                        <div className="overflow-x-hidden  lg:h-full w-full gap-4 flex lg:flex-row flex-col pb-4">
                            <MajorOrderDetailInfo
                                majorOrder={displayedMajorOrder}
                                updatedMajorOrder={updatedMajorOrder as MajorOrderGet}
                                setupdateMajorOrder={setupdatedMajorOrder}
                                updateMajorOrder={handleUpdateMajorOrder}
                                archive={() => modalCon?.trigger(0)}
                            />
                            <div className="w-full flex flex-col gap-4">
                                <MajorOrderDetailHistory history={displayedMajorOrder.history} />
                                <MajorOrderDetailBillList id={id ?? displayedMajorOrder._id} bills={displayedMajorOrder.bills ?? []} />
                            </div>
                        </div>
                    </BaseGrowBody>
                </>
            )}
            <ModalCheckAction
                modalId={0}
                headline={displayedMajorOrder?.archived ? "Archivieren Rückgängig machen?" : "Wirklich Archivieren?"}
                text={
                    displayedMajorOrder?.archived
                        ? "Sind Sie sicher, dass Sie den Auftrag nicht mehr archivieren wollen?"
                        : "Sind Sie sich sicher, dass Sie den Auftrag archivieren wollen?"
                }
                buttonText={displayedMajorOrder?.archived ? "Rückgängig" : "Archivieren"}
                buttonColor={"bg-blue-300 hover:bg-blue-400 border-none"}
                func={handleArchiving}
            />

            <BasePreview
                previewType="document"
                sidePanelId={3}
                title={"Rechnungsvorschau"}
                content={previewElement}
                onClose={() => setpreviewElement(undefined)}
            />
        </BasePage>
    )
}
